import { useEffect, useState } from "react";
import { Grid, Message, Table } from "semantic-ui-react";
import { formatNumberToScientific } from "smart-decimal-format";
import { useGetBillOfMaterialSummaryHistory } from "../../../../api/billOfMaterialSummary";
import { useGetProductSummaryHistory } from "../../../../api/productSummary";
import { CommonTable } from "../../../../components/commonTable/CommonTable";
import { LoadingPlaceholder } from "../../../../components/LoadingPlaceholder/LoadingPlaceholder";
import PaginationView from "../../../../components/pagination/Pagination";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import { ROLES } from "../../../../config/permission-maps";
import {
  CALCULATED_VERSION_PREFIX,
  ProductSummaryHistoryTableCol,
  ProductSummaryTableCol
} from "../../../../config/tableColConstant";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";

const IngredientSummaryHistory = ({ ingredientDetails }: any) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalSummaryHistory, setTotalSummaryHistory] = useState(0);
  const [totalImpact, setTotalImpact] = useState([]);
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;
  const isSupplier =
    ingredientDetails?.supplierIngredientDetails?.data_type === IngredientDataType.SUPPLIER;

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id;
  const productId = ingredientDetails?.supplierIngredientDetails?.product_summary?.productId;
  const salesUnitId = ingredientDetails?.supplierIngredientDetails?.product_summary?.salesUnitId;

  const productSummaryHistoryData = useGetProductSummaryHistory({
    pageNumber,
    pageSize,
    productId,
    salesUnitId,
    isSupplier
  });

  const billOfMaterialSummaryData = useGetBillOfMaterialSummaryHistory({
    pageNumber,
    pageSize,
    ingredientId,
    supplierId,
    isSupplier
  });

  const productSummaryHistory = isSupplier
    ? productSummaryHistoryData.data
    : billOfMaterialSummaryData.data;

  const isProductSummaryHistoryLoading = isSupplier
    ? productSummaryHistoryData.isFetching
    : billOfMaterialSummaryData.isFetching;

  const productSummaryHistoryList = isSupplier
    ? productSummaryHistory?.result?.productSummaryHistory || []
    : productSummaryHistory?.result?.billOfMaterialSummaryHistory || [];

  useEffect(() => {
    if (productSummaryHistory) {
      setPageNumber(productSummaryHistory.page);
      setPageSize(productSummaryHistory.pageSize);
      setTotalSummaryHistory(productSummaryHistory.total);
    }
  }, [productSummaryHistory]);

  const loadHistoryTableData = () => {
    return productSummaryHistoryList.map((data: any, index: any) => {
      const { climateChange, totalImpact } = data;
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>
              {new Date(climateChange?.createdDate).toISOString().slice(0, 19).replace("T", " ")}
            </p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${climateChange.measure?.toFixed(2)} ${climateChange.unit}`}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${CALCULATED_VERSION_PREFIX} ${data?.calculateVersion}`}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              >
                <div
                  key={index}
                  className={"customButtonViewSelect customButtonViewTitle"}
                  onClick={() => setTotalImpact(totalImpact)}
                >
                  <p className={"customButtonTextSelect"}>Details</p>
                </div>
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  const loadTableData = () => {
    return totalImpact.map((data: any, index: any) => {
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data.indicator}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{formatNumberToScientific(data.measure, 5)}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data.unit}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              ></Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  return (
    <>
      {!checkIsUserSupplier ? (
        <>
          <CommonTable tableHeaderData={ProductSummaryHistoryTableCol}>
            {isProductSummaryHistoryLoading ? (
              <Table.Row>
                <Table.Cell colSpan="7">
                  <LoadingPlaceholder />
                </Table.Cell>
              </Table.Row>
            ) : (
              loadHistoryTableData()
            )}
          </CommonTable>
          <div style={{ margin: "10px 0" }}>
            <PaginationView
              currentPageNo={pageNumber}
              totalPages={Math.ceil(totalSummaryHistory / pageSize)}
              pageSize={pageSize}
              currentSelectPage={(page: number) => {
                setPageNumber(page);
              }}
              changePageSize={(pageSize: number) => {
                setPageNumber(1);
                setPageSize(pageSize);
              }}
            />
          </div>

          {totalImpact.length > 0 ? (
            <CommonTable tableHeaderData={ProductSummaryTableCol}>{loadTableData()}</CommonTable>
          ) : null}
        </>
      ) : (
        <Message color="yellow">Calculated: Upgrade to see results</Message>
      )}
    </>
  );
};

export default IngredientSummaryHistory;
